<template>
  <section
    class="card"
    :class="{ create: create }"
    :id="create ? 'create-ivr-card' : ''"
    @click="handleClick"
  >
    <template v-if="create">
      <div class="info">
        <PlusOutlined class="icon" />
        <span>Crear nuevo IVR</span>
      </div>
    </template>

    <template v-else>
      <header class="card__header">
        <div class="card__channels">
          <a-popover v-if="voiceChannels.length" title="Canal de Voz">
            <template #content>
              <ul>
                <li v-for="channel in voiceChannels" :key="channel.associate">
                  {{ channel.associate }} 
                  <span style="color: #8C8C8C">({{ channel.alias }})</span>
                </li>
              </ul>
            </template>

            <img
              src="http://scorev2bucket.obs.sa-peru-1.myhuaweicloud.com/channels/voice.png"
              alt="voice_icon"
            />
          </a-popover>
          <a-popover v-if="whatsappVoiceChannels.length" title="Canal de Whatsapp Voice">
            <template #content>
              <ul>
                <li v-for="channel in whatsappVoiceChannels" :key="channel.associate">
                  {{ channel.associate }} 
                  <span style="color: #8C8C8C">({{ channel.alias }})</span>
                </li>
              </ul>
            </template>

            <img
              src="http://scorev2bucket.obs.sa-peru-1.myhuaweicloud.com/channels/Whatsapp.png"
              alt="whatsapp_voice_icon"
            />
          </a-popover>

          <p v-if="bot?.type === BotType.Automatic">
            {{ bot?.channels_customers?.length ?? 0 }}
            {{ bot?.channels_customers?.length === 1 ? "número vinculado" : "números vinculados" }}
          </p>

          <p v-if="bot?.type === BotType.Survey">
            {{ bot?.portfolios?.length ?? 0 }}
            {{ bot?.portfolios?.length === 1 ? "campaña asociada" : "campañas asociadas" }}

            <a-popover title="Campañas asociadas">
              <template #content>
                <ul>
                  <li v-for="portfolio in bot.portfolios" :key="portfolio.id">
                    {{ portfolio.name }}
                  </li>
                </ul>
              </template>

              <InfoCircleOutlined
                v-if="bot.portfolios?.length"
                style="margin-left: 2px; font-size: 14px"
              />
            </a-popover>
          </p>
        </div>

        <div class="card__menu">
          <a-tooltip title="Editar">
            <a-button class="button" type="link">
              <EditOutlined class="card__icon" @click="handleEdit" />
            </a-button>
          </a-tooltip>

          <a-tooltip title="Duplicar">
            <a-button class="button" type="link">
              <CopyOutlined class="card__icon" @click="handleDuplicate" />
            </a-button>
          </a-tooltip>

          <a-tooltip title="Eliminar">
            <a-button class="button" type="link" :disabled="bot?.status === BotStatus.Active">
              <DeleteOutlined
                class="card__icon"
                @click="handleDelete(props.bot.id, props.bot.name)"
              />
            </a-button>
          </a-tooltip>
        </div>
      </header>

      <div style="height: 100%; position: relative">
        <h4 class="card__name">{{ bot?.name }}</h4>

        <p class="card__description">
          {{ bot?.description }}
        </p>

        <a-button
          v-if="bot?.type === BotType.Automatic"
          style="padding: 0; position: absolute; bottom: 0"
          type="link"
          @click="openScheduleDetails = true"
        >
          Ver detalles de horario
        </a-button>
      </div>

      <footer class="card__footer">
        <div>
          <a-tag :color="currentStatus?.color">
            {{ currentStatus?.text }}
          </a-tag>

          <!-- Sección errores -->
          <!-- <a-button v-if="errors?.length > 0" style="padding: 0; margin-left: 5px" type="link" @click="openErrors = true">
            <a-tag color="warning">
              <WarningFilled /> Errores: {{ errors?.length }}
            </a-tag>
          </a-button> -->
          
          <a-tag v-if="bot?.has_update_api_connector && !isTransferWithoutQueue" color="warning" style="margin-left: 5px">
            <a-tooltip 
              title="Se han detectado cambios en el conector API de este IVR Operador Automático. Revisa la configuración antes de activarlo."
            >
              <WarningFilled /> Cambios detectados
            </a-tooltip>
          </a-tag>

          <!-- If no has queue -->
          <a-tag v-if="isTransferWithoutQueue" color="warning">
            <a-tooltip
              title="Este IVR se encontraba utilizando una cola que ha sido eliminada. Podrás volver a activarlo cuando selecciones una nueva cola de transferencia."
            >
              <WarningFilled /> Cola Eliminada
            </a-tooltip>
          </a-tag>
        </div>

        <!--Status Switch -->
        <DisabledSwitch
          v-if="bot"
          :bot="bot"
          :tooltip="isTransferWithoutQueue ? 'Algunas colas fueron eliminadas, recuerdas reemplazarla para poder activar tu IVR.' : null"
          :disabled="bot?.status !== BotStatus.Active && isTransferWithoutQueue"
          @update="handleUpdate"
        />
      </footer>
    </template>
  </section>

  <DeleteWarning v-model:open="openDeleteWarning" :bot="bot" @delete="handleUpdate" />

  <ScheduleDetailsModal
    v-if="openScheduleDetails"
    ivr
    :visible="openScheduleDetails"
    :chatbot="bot"
    @close="openScheduleDetails = false"
    @edit="handleEdit"
  />

  <!-- Modal errores -->
  <!-- <a-drawer
    v-model:open="openErrors"
    class="custom-class"
    root-class-name="root-class-name"
    title="Errores"
    placement="right"
  >
    <h3 class="name">{{ bot?.name.toUpperCase() }}</h3>
    <p class="errors__list" v-for="error in errors">
      <WarningFilled class="icon__warning" />{{ error }}
    </p>
    <template #footer>
      <a-button style="width: 100%; margin: 10px 0" @click="openErrors = false">Cerrar</a-button>
    </template>
  </a-drawer> -->
</template>

<script setup lang="ts">
import {
  PlusOutlined,
  EditOutlined,
  CopyOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  WarningFilled,
  WarningOutlined,
} from "@ant-design/icons-vue";
import { Modal, message } from "ant-design-vue";

import { botServices } from "@/app/ivr/bot/services/BotServices";
import { Bot } from "@/@types/ivr";
import { StatusProps } from "@/app/chatbots/utils/enums";
import { computed, ref } from "vue";
import { BotStatus, BotType } from "@/app/ivr/bot/utils/enums";

import DisabledSwitch from "@/app/ivr/bot/components/buttons/DisableSwitch.vue";
import DeleteWarning from "../modals/DeleteWarning.vue";
import ScheduleDetailsModal from "@/app/chatbots/components/modals/ScheduleDetailsModal.vue";

const props = defineProps<{ create?: boolean; bot: Bot }>();

const errors = computed(() => props.bot?.errors || [])

const openErrors = ref(false);

const voiceChannels = computed(() =>
  (props.bot?.channels_customers ?? []).filter(
    c => c.channel.name === "Telefonia Entrante"
  )
);

const whatsappVoiceChannels = computed(() =>
  (props.bot?.channels_customers ?? []).filter(
    c => c.channel.name === "WhatsApp Voice"
  )
);

const emit = defineEmits<{
  (e: "create"): void;
  (e: "edit"): void;
  (e: "duplicate"): void;
  (e: "delete"): void;
  (e: "update"): void;
}>();

const currentStatus = computed(() => (props.bot ? StatusProps[props.bot.status] : undefined));
const isTransferWithoutQueue = computed(() => props.bot?.deleted_queues);

const handleClick = () => {
  if (!props.create) return;

  emit("create");
};

const handleEdit = () => emit("edit");

const handleDuplicate = async () => {
  if (!props.bot?.id) return;

  emit("duplicate");

  // await botServices.duplicate(props.bot.id);
  // handleUpdate();
};

const openDeleteWarning = ref(false);

const handleDelete = (ivrId: number, ivrName: string) => {
  if (props.bot.channels_customers.length) {
    openDeleteWarning.value = true;
  } else {
    onDelete(ivrId, ivrName);
  }
};

const isLoading = ref<boolean>(false);

const onDelete = (ivrId: number, ivrName: string) => {
  Modal.confirm({
    title: `¿Estás seguro de eliminar el IVR "${ivrName}"?`,
    content: "Recuerda que ya no podrás recuperarlo más adelante.",
    okText: "Eliminar",
    onOk: async () => {
      try {
        isLoading.value = true;
        await botServices.delete(ivrId);
        message.success("Se eliminó exitosamente");
        handleUpdate();
      } catch (error) {
        // const errorMessage = error?.getErrorMessage() || "Error al eliminar";
        message.error("Error al eliminar");
        console.log(error);
      } finally {
        isLoading.value = false;
      }
    },
    centered: true,
    cancelText: "Cancelar",
    width: 436,
  });
};

const handleUpdate = () => emit("update");

const openScheduleDetails = ref(false);
</script>

<style scoped lang="sass">
p
  margin: 0

.button
  padding: 0
  height: 22px

.card
  height: 210px
  background: $gray-2
  border-radius: 4px
  border: $border-medium
  position: relative
  padding: 12px 20px
  display: grid
  grid-template-rows: auto 1fr auto
  grid-template-columns: 100%

  &.create
    display: flex
    align-items: center
    justify-content: center
    border-color: $blue-6
    color: $blue-6
    border-style: dashed
    background-color: $blue-1
    cursor: pointer

    .info
      display: flex
      flex-direction: column
      align-items: center
      justify-content: center
      color: $blue-6

      .icon
        color: $blue-6
        font-size: 40px
        margin-bottom: 8px

  &__header
    display: flex
    justify-content: space-between
    align-items: center
    margin-bottom: 10px

  &__channels
    color: $gray-8
    font-size: 12px
    display: flex
    align-items: center
    gap: 4px

    img
      width: 18px

  &__menu
    display: flex
    justify-content: flex-end
    gap: 8px

  &__name
    color: $gray-9
    margin-bottom: 2px

  &__description
    color: $gray-7
    word-wrap: break-word

  &__footer
    padding: 12px 4px 0
    border-top: 1px solid $gray-5
    display: flex
    justify-content: space-between
    align-items: center

.errors__list
  margin-top: 15px
  display: flex
  align-items: start
  .icon__warning
    color: rgba(250, 173, 20, 1)
    margin-top: 4px
    margin-right: 5px
</style>
